import React from 'react';
import { Card, CardContent, Typography, Box, Chip, IconButton, Button } from '@mui/joy';
import {
  Eye as ViewIcon,
  BellRinging as SubscribedIcon,
  BellSlash as UnsubscribedIcon,
  MapPin as LocationIcon,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import type { StormTerritory } from '../../types/stormtracker';

interface TerritoryCardProps {
  territory: StormTerritory;
  onSubscribe?: (id: number) => void;
  onUnsubscribe?: (id: number) => void;
  isSubscribing?: boolean;
}

const getLocationSummary = (territory: StormTerritory): string => {
  if (territory.zip_codes && territory.zip_codes.length > 0) {
    const count = territory.zip_codes.length;
    return `${count} ZIP code${count > 1 ? 's' : ''}`;
  }
  if (territory.counties && territory.counties.length > 0) {
    const count = territory.counties.length;
    return `${count} ${count > 1 ? 'counties' : 'county'}`;
  }
  if (territory.radius_miles) {
    return `${territory.radius_miles} mi radius`;
  }
  return 'No area defined';
};

export const TerritoryCard: React.FC<TerritoryCardProps> = ({
  territory,
  onSubscribe,
  onUnsubscribe,
  isSubscribing = false,
}) => {
  const navigate = useNavigate();

  const handleViewDetails = () => {
    navigate(`/advanced/storm-tracker/territories/${territory.id}`);
  };

  const handleSubscriptionToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (territory.is_subscribed && onUnsubscribe) {
      onUnsubscribe(territory.id);
    } else if (!territory.is_subscribed && onSubscribe) {
      onSubscribe(territory.id);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'primary.500',
          boxShadow: 'md',
        },
      }}
      onClick={handleViewDetails}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationIcon color="var(--joy-palette-primary-500)" />
            <Typography level="title-lg">{territory.name}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {territory.is_active ? (
              <Chip size="sm" color="success" variant="soft">
                Active
              </Chip>
            ) : (
              <Chip size="sm" color="neutral" variant="soft">
                Inactive
              </Chip>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip size="sm" variant="outlined" startDecorator={<LocationIcon size={14} />}>
            {getLocationSummary(territory)}
          </Chip>
          {territory.subscriber_count !== undefined && territory.subscriber_count > 0 && (
            <Chip size="sm" variant="soft" color="primary">
              {territory.subscriber_count} subscriber{territory.subscriber_count > 1 ? 's' : ''}
            </Chip>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Thresholds
            </Typography>
            <Typography level="body-xs">
              Hail: {territory.min_hail_size_inches}" | Wind: {territory.min_wind_speed_mph} mph
              {territory.include_tornadoes && ' | Tornado'}
            </Typography>
          </Box>
        </Box>

        {territory.alert_count_7d !== undefined && (
          <Box sx={{ mb: 2 }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Recent Activity
            </Typography>
            <Typography level="title-sm" color={territory.alert_count_7d > 0 ? 'warning' : 'neutral'}>
              {territory.alert_count_7d} alert{territory.alert_count_7d !== 1 ? 's' : ''} in last 7 days
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            size="sm"
            variant={territory.is_subscribed ? 'soft' : 'outlined'}
            color={territory.is_subscribed ? 'success' : 'neutral'}
            startDecorator={territory.is_subscribed ? <SubscribedIcon /> : <UnsubscribedIcon />}
            onClick={handleSubscriptionToggle}
            loading={isSubscribing}
          >
            {territory.is_subscribed ? 'Subscribed' : 'Subscribe'}
          </Button>
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails();
            }}
          >
            <ViewIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TerritoryCard;
